import { useEffect } from 'react';

/**
 * Adds `.in` to every `.reveal` element as it enters the viewport.
 *
 * The IntersectionObserver alone is not enough: a fast flick or an anchor jump
 * can move an element past the viewport before the observer reports it, and it
 * would then stay invisible forever. The scroll sweep below catches anything at
 * or above the fold, which includes everything already scrolled past.
 */
export function useReveal(): void {
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targets = Array.from(document.querySelectorAll<HTMLElement>('.reveal'));
    if (!targets.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('in'));
      return;
    }

    const pending = new Set(targets);
    const show = (el: HTMLElement) => {
      el.classList.add('in');
      pending.delete(el);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            io.unobserve(entry.target);
            show(entry.target as HTMLElement);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0 },
    );
    targets.forEach((el) => io.observe(el));

    let ticking = false;
    const sweep = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const vh = window.innerHeight;
        pending.forEach((el) => {
          if (el.getBoundingClientRect().top < vh * 0.94) {
            io.unobserve(el);
            show(el);
          }
        });
        ticking = false;
      });
    };

    window.addEventListener('scroll', sweep, { passive: true });
    window.addEventListener('resize', sweep);
    sweep();

    return () => {
      io.disconnect();
      window.removeEventListener('scroll', sweep);
      window.removeEventListener('resize', sweep);
    };
  }, []);
}
